/**
 * The priority of a manifest.
 *
 * @remarks
 * This is used inside the list of sources to determine which manifest to load first.
 * There should always be at least one main manifest and every other manifest should be marked as a fallback option.
 *
 * The idea is to provide a seamless experience for the user, by switching through different manifests if one fails.
 */
export const ManifestPriority = {
  /**
   * The main manifest.
   *
   * @remarks
   * Priority main is always selected first. This should be the manifest you prefer for your use case
   * e.g. it uses the best performing CDN with the lowest cost.
   *
   * If the main manifest fails, the player will instead try one of the fallback manifests.
   *
   * Note: The fallback feature between main and fallback is not yet supported.
   */
  Main: 'main',

  /**
   * A fallback manifest which should only be used if the main manifest cannot be played.
   *
   * @remarks
   * If you replicate your main manifest to a different CDNs,
   * you specify your preferred choice as the main manifest and all others as fallback manifests.
   *
   * Fallback manifests are tried in the order specified.
   *
   * A fallback manifest will only be tried if the main manifest fails or no main manifest is specified.
   *
   * Note: The fallback feature between main and fallback is not yet supported.
   *
   * @alpha
   */
  Fallback: 'fallback',
} as const;

/**
 * {@inheritDoc ManifestPriority:var}
 */
export type ManifestPriority = (typeof ManifestPriority)[keyof typeof ManifestPriority];
